import React, { useRef, useState } from 'react';
import Button from '@fff-web/fff-ui-shared/Button';
import { SubscriptionUpdatePaymentModal } from '@fff-web/fff-payments';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useHistory } from 'react-router-dom';
import { identify } from '@fff-web/fff-analytics';
import {
  useReactivateMyMembershipContext,
  IReactivateMembershipContext,
} from '../../utils/ContextProviders/ReactivateContext';
import useGetPricesByPlan from '../../utils/hooks/useGetPricesByPlan';
import Layout from '../../components/Layout';
import TermsAndConditions from './TermsAndConditions';
import HeroBanner from './HeroBanner';
import HowItWorks from './HowItWorks';
import MembershipPlans from './MembershipPlans';
import PriceSummary from './PriceSummary';
import env from '../MyAccount/utils/env';
import useShowErrorOrRedirectThankYouPageV2 from '../../hooks/useShowErrorOrRedirectThankYouPageV2';
import { reactivateExpiredSubscription } from '../../services/subscription';
import {
  sendReactivateSegmentEventAfterResponse,
  sendGTMReactivateConversion,
} from '../Reactivate/Events';
import { getPlanChangeCompletedEventStatusFromResponse } from '../../helpers';
import AllErrorMessage from '../../components/AllErrorMessage';
import styles from './ReactivateMembershipV2.module.scss';
import { GROWTH_MY_REACTIVATE_MEMBERSHIP } from '../../utils/Optimizely/featureFlagVariations';
import Reactivate from '../Reactivate/Reactivate';

const SUBMIT_CTA_NAME = 'REACTIVATE';

interface ReactivateMembershipV2Props {
  ldClient: string;
}

const ReactivateMembershipV2: React.FC<ReactivateMembershipV2Props> = ({
  ldClient,
}) => {
  const history = useHistory();
  const {
    growthMyMembership,
    selectedPlanCode,
    couponsApplied,
    myAccountPath,
    showReactivationContent,
  } = useReactivateMyMembershipContext();
  const { isLoadingPrices } = useGetPricesByPlan();
  const { variables } = growthMyMembership as IReactivateMembershipContext;
  const planSectionRef = useRef<HTMLDivElement>(null);
  const reactivateCaptchaRef = useRef(null);
  const [submitPactSafeContract, setSubmitPactSafeContract] = useState(false);
  const { heroBanner, howItWorks } = variables;
  const onScrollToSection = () => {
    const section = planSectionRef.current;
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const thanksPage = `${myAccountPath}/reactivate-thanks`;

  const {
    isLoading,
    setLoading,
    showRetry,
    showPayment,
    errorPaymentMessage,
    onClose,
    handlePlanChangeResponse,
  } = useShowErrorOrRedirectThankYouPageV2(history, thanksPage, couponsApplied);
  
  const handleSubmit = () => {
    setSubmitPactSafeContract(true);
    setTimeout(() => {
    reactivateCaptchaRef.current.resetCaptcha();
    reactivateCaptchaRef.current
      .execute({ async: true })
      .then(({ response: captchaToken }) => {
        setLoading(true);
        handleReactivateExpiredSubscription(SUBMIT_CTA_NAME, captchaToken);
      })
      .catch((err) => {
        console.error('Error processing your request:', err);
      });
    }, 1000);
  };

  const handleReactivateExpiredSubscription = async (
    ctaName: string,
    captchaToken: string
  ) => {
    try {
      const response = await reactivateExpiredSubscription(
        selectedPlanCode,
        couponsApplied,
        captchaToken
      );
      const status = getPlanChangeCompletedEventStatusFromResponse(response);
      const eventsToFire = [
        sendReactivateSegmentEventAfterResponse(status, ctaName),
      ];

      if (status === 'success') {
        eventsToFire.push(identify({ traits: { planCode: selectedPlanCode } }));
        sendGTMReactivateConversion(response);
      }

      await Promise.allSettled(eventsToFire);
      handlePlanChangeResponse(response);
    } catch (error) {
      console.error('Error reactivating subscription:', error);
    }
  };

  const showControlVariation =
    growthMyMembership.enabled &&
    growthMyMembership.variationKey !== GROWTH_MY_REACTIVATE_MEMBERSHIP.CONTROL;

  return (
    <Layout
      isLoading={isLoadingPrices || isLoading || showReactivationContent}
      pageTitle="Expired Reactivation"
      redirectIfNoAuth
      showError={showRetry}
      ErrorComponent={() => (
        <AllErrorMessage subjectAction="reactivate" onButtonClick={onClose} />
      )}
    >
      {showControlVariation ? (
        <main className={styles.reactivateMainWrapper}>
          <HeroBanner
            ctaOnClick={onScrollToSection}
            headline={heroBanner.headline}
            caption={heroBanner.caption}
            disclaimer={heroBanner.disclaimer}
            mainCTA={heroBanner.mainCTA}
          />
          <HowItWorks productsList={howItWorks?.productsList} />
          <MembershipPlans
            planSectionRef={planSectionRef}
            reactivateCaptchaRef={reactivateCaptchaRef}
          />
          <section className={styles.summarySectionWrapper}>
            <PriceSummary />
            <TermsAndConditions submitPactSafeContract={submitPactSafeContract}/>
            <HCaptcha
              sitekey={env.hCaptchaSiteKey}
              onVerify={() => undefined}
              size="invisible"
              ref={reactivateCaptchaRef}
            />
          </section>
          <section className={styles.ctaSectionWrapper}>
            <Button disabled={isLoading} onClick={handleSubmit}>
              {SUBMIT_CTA_NAME}
            </Button>
          </section>
          <SubscriptionUpdatePaymentModal
            onClose={onClose}
            subtitle={errorPaymentMessage}
            isOpen={showPayment}
            onSuccess={handleSubmit}
            hasError={!!errorPaymentMessage}
          />
        </main>
      ) : (
        <Reactivate
          isLoadingSubmit={isLoading}
          ldClient={ldClient}
          handleSubmit={handleSubmit}
          reactivateCaptchaRef={reactivateCaptchaRef}
        />
      )}
    </Layout>
  );
};

export default ReactivateMembershipV2;
